import {cloneDeep, includes} from 'lodash/fp';

import {CriteriaFilter} from 'api/gen/CriteriaFilter';
import {logger} from 'app/sentry-logger';

import {Criterias} from '../types/criterias';

// eslint-disable-next-line no-magic-numbers
const numberRangePercentages = [1, 5, 10, 20, 30] as const;

type NumberRangePercentage = typeof numberRangePercentages[number];

const isValidNumberRangePercentage = (value: number): value is NumberRangePercentage =>
    includes(value, numberRangePercentages);

type BooleanFilter = Extract<CriteriaFilter, {tag: 'BOOL'}>;
type DateFilter = Extract<CriteriaFilter, {tag: 'DATE'}>;
type PercentageFilter = Extract<CriteriaFilter, {tag: 'SINGLE_INT'}>;
type NumberRangeFilter = Exclude<CriteriaFilter, BooleanFilter | DateFilter | PercentageFilter>;

const applyBooleanFilter = (criterias: Criterias, filter: BooleanFilter): void => {
    if (criterias.radios[filter.code] !== undefined) {
        criterias.radios[filter.code] = filter.value;
    } else {
        logger.logError(new Error(`session RADIO criteria '${filter.code}' not present in form`));
    }
};

const applyDateFilter = (criterias: Criterias, filter: DateFilter): void => {
    if (criterias.dateRanges[filter.code] !== undefined) {
        criterias.dateRanges[filter.code] = [filter.leftValue, filter.rightValue];
    } else {
        logger.logError(new Error(`session DATE criteria '${filter.code}' not present in form`));
    }
};

const applyPercentageFilter = (criterias: Criterias, filter: PercentageFilter): void => {
    const currentValue = criterias.numberRanges[filter.code];
    if (currentValue === undefined) {
        logger.logError(new Error(`session SINGLE_INT criteria '${filter.code}' not present in form`));
    } else if (currentValue.criteriaType !== 'INT') {
        logger.logError(new Error(
            `session SINGLE_INT criteria '${filter.code}' does not match default type ${currentValue.criteriaType}`,
        ));
    } else if (isValidNumberRangePercentage(filter.value)) {
        criterias.numberRanges[filter.code] = {
            ...currentValue,
            percentage: filter.value,
            selectedType: 'percentage',
        };
    } else {
        logger.logError(new Error(
            `session SINGLE_INT criteria '${filter.code}' has invalid percent ${String(filter.value)}`,
        ));
    }
};

const applyNumberRangeFilter = (criterias: Criterias, filter: NumberRangeFilter): void => {
    const currentValue = criterias.numberRanges[filter.code];
    if (currentValue === undefined) {
        logger.logError(new Error(`session ${filter.tag} criteria '${filter.code}' not present in form`));
    } else if (currentValue.criteriaType === filter.tag) {
        criterias.numberRanges[filter.code] = {
            ...currentValue,
            minimum: filter.leftValue,
            maximum: filter.rightValue,
            selectedType: 'number',
        };
    } else {
        logger.logError(new Error(
            `session ${filter.tag} criteria '${filter.code}' does not match default type ${currentValue.criteriaType}`,
        ));
    }
};

/**
 * Merges filters restored from the mating session into the default criteria form values.
 *
 * @param criterias Default criteria form values built from the current definitions.
 * @param filters Active filters returned by the mating session endpoint.
 * @returns Criteria values suitable for the accordion form.
 */
export const getSessionFilterFormValues = (
    criterias: Criterias,
    filters: Array<CriteriaFilter>,
): Criterias => {
    const criteriaFormValues = cloneDeep(criterias);

    filters.forEach(filter => {
        if (filter.tag === 'BOOL') {
            applyBooleanFilter(criteriaFormValues, filter);
        } else if (filter.tag === 'DATE') {
            applyDateFilter(criteriaFormValues, filter);
        } else if (filter.tag === 'SINGLE_INT') {
            applyPercentageFilter(criteriaFormValues, filter);
        } else {
            applyNumberRangeFilter(criteriaFormValues, filter);
        }
    });

    return criteriaFormValues;
};
